import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import * as authApi from '../api/auth';
import { ApiError, setCsrfTokenGetter } from '../api/client';

/**
 * Session context for the FE app.
 *
 * Responsibilities:
 *
 *   1. On mount, call `GET /auth/me` to hydrate the session. A 401 is an
 *      expected outcome (the user is not logged in yet) — we flip to
 *      `anonymous` without logging noise. Anything else surfaces as
 *      `anonymous` too, but with a console warning so flaky auth surfaces
 *      during local dev.
 *   2. Hold the current CSRF token in a ref and register a getter with the
 *      `apiFetch` module, so every mutating fetch has access without
 *      re-rendering the whole tree on token rotation.
 *   3. Expose `logout` / `refresh` as the only sanctioned ways to mutate
 *      session state. Components MUST NOT write directly into this context —
 *      the context value is a read view, the actions are the only write path.
 *      There is no `login` action: under Entra SSO, sign-in is a full-page
 *      browser navigation (owned by `LoginPage`), not a context call.
 *
 * Non-goals:
 *   - Token rotation before expiry. v1 runs with a single long-lived JWS
 *     per session; rotation can land later without changing this surface.
 *   - Persistence across tab reloads. The `sid` cookie is HttpOnly and owned
 *     by the server; we re-hydrate via `/auth/me` on every mount.
 */

export type SessionStatus = 'loading' | 'anonymous' | 'authenticated';

export interface AuthUser {
  email: string;
  displayName: string;
  role: 'ADMIN' | 'STANDARD';
}

export interface AuthState {
  status: SessionStatus;
  user: AuthUser | null;
  csrfToken: string | null;
  sessionExpiresAt: string | null;
}

export interface AuthContextValue extends AuthState {
  logout(): Promise<void>;
  refresh(): Promise<void>;
}

const INITIAL_STATE: AuthState = {
  status: 'loading',
  user: null,
  csrfToken: null,
  sessionExpiresAt: null,
};

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [state, setState] = useState<AuthState>(INITIAL_STATE);

  // Keep a ref in sync with state so the CSRF getter (registered exactly
  // once) reads fresh values without depending on a stale closure.
  const stateRef = useRef<AuthState>(state);
  stateRef.current = state;

  // Register the CSRF getter with apiFetch on mount. The getter is a pure
  // read of the ref — there is no React-only state smuggled in here, and
  // the module-level indirection means apiFetch stays usable outside React.
  useEffect(() => {
    setCsrfTokenGetter(() => stateRef.current.csrfToken);
    // No cleanup: unmounting AuthProvider means we are tearing the whole
    // app down (hot-reload) and the next mount will overwrite the getter.
    // Clearing it here would race with any fetch already in flight.
  }, []);

  // Initial hydration via /auth/me. Runs once.
  const hydrate = useCallback(async () => {
    try {
      const res = await authApi.me();
      setState({
        status: 'authenticated',
        user: { email: res.email, displayName: res.displayName, role: res.role },
        csrfToken: res.csrfToken,
        sessionExpiresAt: res.sessionExpiresAt,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setState({
          status: 'anonymous',
          user: null,
          csrfToken: null,
          sessionExpiresAt: null,
        });
        return;
      }
      console.warn('AuthProvider: /auth/me failed unexpectedly', err);
      setState({
        status: 'anonymous',
        user: null,
        csrfToken: null,
        sessionExpiresAt: null,
      });
    }
  }, []);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch (err) {
      // A logout that 401s because the session already expired is a no-op
      // from the UI's point of view — we still want to clear local state.
      // Anything else we log and still clear: leaving the UI in a half-auth
      // state is worse than a stale audit gap.
      if (!(err instanceof ApiError && err.status === 401)) {
        console.warn('AuthProvider: logout failed, clearing local state anyway', err);
      }
    }
    setState({
      status: 'anonymous',
      user: null,
      csrfToken: null,
      sessionExpiresAt: null,
    });
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    await hydrate();
  }, [hydrate]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      logout,
      refresh,
    }),
    [state, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Access the current auth state + actions. Throws if called outside an
 * `AuthProvider` — this is a programming error, not a runtime condition, so
 * we want it to blow up loudly in dev rather than return a sentinel.
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth must be used within <AuthProvider>');
  }
  return ctx;
}
